/**
 * A custom element representing a product form with methods to show or hide the form.
 *
 * @class
 * @extends HTMLElement
 * @description The `ProductFormCustom` class controls the visibility of the form and handles additional options like
 * Christmas tree stands, delivery, and collections. It updates the subtotal based on user selections.
 */

// Utils
/**
 * Takes an element and returns an array of form elements inside it.
 * @param {HTMLElement} element - An HTMLElement
 * @returns {HTMLFormElement[]} - An array of form elements inside the HTMLElement
 */
const getFormElements = (element) => {
  return element ? Array.from(element.querySelectorAll('form')) : []
}

/**
 * Checks if the form has a variant price.
 * @param {HTMLFormElement} form - The form to check.
 * @returns {boolean} - Whether the form has a variant price.
 */
const formHasVariantPrice = (form) => {
  const variantPrice = form.dataset.variantPrice

  // In case the wrong form slips in, we don't want to break the code.
  if (!variantPrice) return false

  const variantPriceNumber = parseInt(variantPrice, 10)

  return variantPriceNumber !== 0
}

/**
 * Queries the provided element for a details element.
 * @param {HTMLElement} elementWithDetailsChild - The element to query for a details child.
 * @returns {HTMLElement | null} - The details element or null if not found.
 */
const queryDetailElement = (elementWithDetailsChild) => {
  const details = elementWithDetailsChild.querySelector('details')
  if (details) {
    return details
  }
  return null
}

/**
 * Checks the details element of the element, and checks if it has the open attribute, if it does not, return false
 * @param {HTMLElement} formContainingElement - The element containing the form
 * @returns {boolean} - Whether the details element is open
 */
const ignoreIfDetailsIsNotOpen = (formContainingElement) => {
  const details = queryDetailElement(formContainingElement)
  if (details) {
    return details?.hasAttribute('open')
  }
  return true
}

/**
 * Goes through an array of HTMLFormElements and returns a tuple containing arrays of form elements with data-form-type='variantId' and 'cartAttributes'.
 * Filters variantIdForms to only include forms with a variant price.
 * @param {HTMLFormElement[]} formElements - An array of HTMLFormElements.
 * @returns {[HTMLFormElement[], HTMLFormElement[]]} - A tuple containing arrays of form elements.
 */
const getFormElementsWithVariantIdAndCartAttributes = (formElements) => {
  // Filter forms with data-form-type='variantId' and a variant price
  const variantIdForms = formElements.filter(
    (formElement) => formElement.dataset.formType === 'variantId' && formHasVariantPrice(formElement)
  )

  // Filter forms with data-form-type='cartAttributes'
  const cartAttributesForms = formElements.filter((formElement) => formElement.dataset.formType === 'cartAttributes')

  return [variantIdForms, cartAttributesForms]
}

/**
 * Extracts properties from a form data object.
 * @param {HTMLFormElement} form - The form to extract properties from.
 * @returns {Object} - An object containing the properties.
 */
const getPropertiesFromForm = (form) => {
  const formData = new FormData(form)
  const properties = {}
  for (let [key, value] of formData.entries()) {
    if (key.startsWith('properties[')) {
      const propertyName = key.substring(11, key.length - 1)
      properties[propertyName] = value
    }
  }

  return properties
}

/**
 * Serializes the form and extracts the value of a specific field.
 * @param {HTMLFormElement} form - The form to serialize.
 * @param {string} fieldName - The name of the field to extract.
 * @returns {string | null} - The value of the specified field, or null if not found.
 */
const getFieldValueFromSerializedForm = (form, fieldName) => {
  if (!form) return null
  const formData = new FormData(form)
  const value = formData.get(fieldName)
  return value !== null ? value.toString() : null
}

/**
 * Extracts all fields from the form data, processes keys starting with 'properties[', and returns an object.
 * @param {HTMLFormElement} form - The form to extract fields from.
 * @param {string[]} excludeFields - An array of field names to exclude.
 * @returns {Object} - An object containing the form data fields and values.
 */
const getFormDataAsObject = (form, excludeFields = []) => {
  if (!form) return {}
  const formData = new FormData(form)
  const data = {}
  for (let [key, value] of formData.entries()) {
    if (!excludeFields.includes(key)) {
      if (key.startsWith('properties[')) {
        const propertyName = key.substring(11, key.length - 1)
        data[propertyName] = value
      } else {
        data[key] = value
      }
    }
  }
  return data
}

/**
 * Parses a value to an integer.
 * @param {string | number} value - The value to parse.
 * @returns {number} - The parsed integer.
 */
const toInt = (value) => {
  return parseInt(value, 10)
}

/**
 * Adds multiple products to the Shopify cart with optional cart attributes.
 *
 * @param {Array<string>} variantIds - An array of product variant IDs to add to the cart.
 * @param {Object} [cartAttributes={}] - Optional cart attributes to add to the cart.
 * @returns {Promise<Object|boolean>} - Returns the cart object if successful, otherwise false.
 */
async function addProductsToCart(items, cartAttributes = {}) {
  const payload = {
    items,
  }

  if (Object.keys(cartAttributes).length > 0) {
    payload.attributes = cartAttributes
  }

  try {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    })

    if (!response.ok) {
      console.error('response', await response.text())
      throw new Error('Failed to add items to cart')
    }

    const cartData = await response.json()

    return cartData
  } catch (error) {
    console.error('error', error)
    return false
  }
}

/**
 * Fetches the HTML content for a product page with the given product handle and variant ID.
 * @param {string} productHandle - The handle of the product.
 * @param {number} variantId - The variant ID of the product.
 * @returns {Promise<string>} - The HTML of the product page.
 */
const fetchProductPage = async (productHandle, variantId) => {
  const response = await fetch(`${window.location.origin}/products/${productHandle}?variant=${variantId}`)
  return response.text()
}

/**
 * Parses the provided HTML and extracts the subtotal value element.
 * @param {string} html - The HTML string of the product page.
 * @returns {HTMLElement | null} - The subtotal value element or null if not found.
 */
const getSubTotalValueElementFromHtml = (html) => {
  const parser = new DOMParser()
  const doc = parser.parseFromString(html, 'text/html')
  return doc.querySelector('[data-component-subtotal-value]')
}

class ProductFormCustom extends HTMLElement {
  /**
   * Constructs the custom product form element.
   * @constructor
   */
  constructor() {
    super()

    /** @type {number} The subtotal of the product. */
    this.subTotal = 0

    /**
     * @type {Object<string, number>}
     * The price of each selectable option.
     * Key is the option name, value is the price.
     */
    this.additionalOptionsPrices = {
      christMasTreeStand: 0,
      collection: 0,
      deliveryAssistance: 0,
      deliveryCharge: 0,
    }

    /** @type {HTMLButtonElement | null} The add to cart button */
    this.addToCartButton = null

    /** @type {number} The combined value of all the additional options. */
    this.additionalOptionsTotal = Object.values(this.additionalOptionsPrices).reduce((acc, price) => acc + price, 0)

    /** @type {string} The product handle from the element attribute. */
    this.productHandle = this.getAttribute('data-product-handle')

    /** @type {HTMLElement[]} The detail elements of which at least one has to be open for the add to cart to be enabled */
    this.detailElements = []

    /** @type {HTMLElement | null} Reference to the post-code-picker-custom element */
    this.postCodePickerElement = null

    /** @type {baseProductFormContainer| null} Reference to the variant-selects-custom element */
    this.baseProductFormContainer = null

    /** @type {ProductForm | null} Reference to the base product form, child of variant-selects-custom */
    this.baseProductForm = null

    /** @type {ProductChristmasTreeStandOptionsCustom | null} Reference to the product-christmas-tree-stand-options-custom element */
    this.christMasTreeElement = null

    /** @type {collectionElement | null} Reference to the product-collection-options-custom element */
    this.collectionElement = null

    /** @type {HTMLElement | null} The delivery options element */
    this.deliveryOptionsElement = null

    /** @type {HTMLElement | null} The click and collect element */
    this.clickAndCollectElement = null

    /** @type {HTMLElement | null} The product shipping charge element */
    this.productShippingChargeElement = null
  }

  /**
   * Runs when the element is connected to the DOM.
   * Sets static elements and initializes subtotal and additional options.
   */
  connectedCallback() {
    this.setStaticElements()
    this.subTotal = parseInt(this.subTotalElement.dataset.value, 10)
    this.setAdditionalOptionsTotal()
    this.setEventListeners()
  }

  /**
   * Validates the mandatory fields for the delivery options or click and collect.
   * @returns {boolean} - Whether the mandatory fields are valid.
   */
  validateMandatoryFieldsForDeliveryOptionsOrClickAndCollect() {
    if (this.deliveryOptionsElement.details.hasAttribute('open')) {
      this.deliveryOptionsElement.checkIfAllMandatoryOptionsSelected()
      return this.deliveryOptionsElement.dataset.showMandatoryFieldsWarning === 'true' ? false : true
    }

    if (this.clickAndCollectElement.details.hasAttribute('open')) {
      this.clickAndCollectElement.checkIfAllMandatoryOptionsSelected()

      return this.clickAndCollectElement.dataset.showMandatoryFieldsWarning === 'true' ? false : true
    }

    return true
  }

  /**
   * Adds products and cart properties to the cart.
   */
  addProductsAndCartPropertiesToCart() {
    const continueOrNot = this.validateMandatoryFieldsForDeliveryOptionsOrClickAndCollect()

    if (continueOrNot) {
      this.addToCartButton.disabled = false
    } else {
      this.addToCartButton.disabled = true
      setTimeout(() => {
        this.addToCartButton.disabled = false
      }, 1000)
      return
    }

    this.setStaticElements()

    const elementsContainingForms = [
      this.baseProductFormContainer,
      this.christMasTreeElement,
      this.collectionElement,
      this.deliveryOptionsElement,
      this.clickAndCollectElement,
      this.productShippingChargeElement,
      this.postCodePickerElement,
    ]

    const filteredElementsContainingForms = elementsContainingForms.filter(ignoreIfDetailsIsNotOpen)

    const formsArrays = filteredElementsContainingForms.map(getFormElements)

    const forms = formsArrays.flat().filter(Boolean)

    const uniqueForms = Array.from(new Set(forms))

    const [variantIdForms, cartAttributesForms] = getFormElementsWithVariantIdAndCartAttributes(uniqueForms)

    const items = variantIdForms
      .map((form) => {
        const variantId = getFieldValueFromSerializedForm(form, 'id')
        if (!variantId) {
          return null
        }
        const properties = getPropertiesFromForm(form)
        return {
          id: parseInt(variantId, 10),
          quantity: 1,
          properties: Object.keys(properties).length > 0 ? properties : undefined,
        }
      })
      .filter(Boolean)

    let cartAttributes = {}

    cartAttributesForms.forEach((form) => {
      const attributes = getFormDataAsObject(form, ['form_type', 'utf8'])
      cartAttributes = { ...cartAttributes, ...attributes }
    })

    addProductsToCart(items, cartAttributes)
      .then((cartData) => {
        if (cartData) {
          window.location.href = '/cart'
        } else {
          throw new Error('Failed to add items to cart')
        }
      })
      .catch((error) => {
        alert(
          `Sorry! Something went wrong somewhere, please reload the page and try again, if the issue persists please <a href="/pages/contact-us">contact us.</a>. Error Message: ${error}`
        )
      })
  }

  /**
   * Sets event listeners for the add to cart button.
   */
  setEventListeners() {
    this.addToCartButton.addEventListener('click', this.addProductsAndCartPropertiesToCart.bind(this))
  }

  /**
   * Enables the add to cart button.
   */
  enableAddToCart() {
    this.addToCartButton.removeAttribute('disabled')
  }

  /**
   * Disables the add to cart button.
   */
  disableAddToCart() {
    this.addToCartButton.setAttribute('disabled', '')
  }

  /**
   * Checks if this.detailElements has at least one that is open, that is has the open attribute if it does, call enableAddToCart
   * */
  checkIfAddToCartShouldBeEnabled() {
    this.detailElements.forEach((element) => {})
    if (this.detailElements.some((element) => element.hasAttribute('open'))) {
      this.enableAddToCart()
    } else {
      this.disableAddToCart()
    }
  }

  /**
   * Shows the product form by removing the 'hidden' class from the element.
   * @method
   */
  show() {
    this.classList.remove('hidden')
  }

  /**
   * Hides the product form by adding the 'hidden' class to the element.
   * @method
   */
  hide() {
    this.classList.add('hidden')
  }

  /**
   * Sets up static elements like subtotal and option elements.
   * @method
   */
  setStaticElements() {
    const subTotalElement = this.querySelector('[data-component-subtotal-value]')
    if (subTotalElement) {
      this.setSubTotalElement(subTotalElement)
    }

    const postCodePickerElement = document.querySelector('post-code-picker-custom')
    if (postCodePickerElement) {
      this.setPostCodePickerElement(postCodePickerElement)
    }

    this.baseProductFormContainer = document.querySelector('[data-component-base-product-form-custom-inner]')
    if (this.setBaseProductFormContainer) {
      this.baseProductForm = this.setBaseProductFormContainer.querySelector('[data-component-base-product-form-custom]')
    }

    const christMasTreeStandElement = document.querySelector('product-christmas-tree-stand-options-custom')
    if (christMasTreeStandElement) {
      this.setChristMasTreeElement(christMasTreeStandElement)
    }

    const collectionElement = document.querySelector('[data-component-collection-options-custom]')
    if (collectionElement) {
      this.setCollectionElement(collectionElement)
    }

    const deliveryOptionsElement = document.querySelector('product-delivery-options-custom')
    if (deliveryOptionsElement) {
      this.setDeliveryOptionsElement(deliveryOptionsElement)
    }

    const clickAndCollectElement = document.querySelector('product-click-and-collect-custom')
    if (clickAndCollectElement) {
      this.setClickAndCollectElement(clickAndCollectElement)
    }

    const productShippingChargeElement = document.querySelector('product-shipping-charge-custom')
    if (productShippingChargeElement) {
      this.setProductShippingChargeElement(productShippingChargeElement)
    }

    this.detailElements = [this.deliveryOptionsElement, this.clickAndCollectElement].map(queryDetailElement)
    this.detailElements = this.detailElements.filter((element) => element !== null)

    this.addToCartButton = this.querySelector('[data-component-add-to-cart]')
  }

  /**
   * Sets the price for the additional options like tree stand and delivery.
   * @method
   */
  setAdditionalOptionsPrices() {
    const christMasTreeStandElement = document.querySelector('[data-component-christmas-tree-stand-options-custom]')
    const christMasTreeStandSelectElement = christMasTreeStandElement?.querySelector('select')
    if (christMasTreeStandSelectElement) {
      const selectedOption = christMasTreeStandSelectElement.options[christMasTreeStandSelectElement.selectedIndex]
      const price = parseFloat(selectedOption.dataset.price) || 0
      this.setChristMasTreeStandPrice(price)
    }

    const collectionElement = document.querySelector('[data-component-collection-options-custom]')
    const collectionSelectElement = collectionElement?.querySelector('select')
    if (collectionSelectElement) {
      const selectedOption = collectionSelectElement.options[collectionSelectElement.selectedIndex]
      const price = parseFloat(selectedOption.dataset.price) || 0
      this.setCollectionPrice(price)
    }

    const deliveryOptionsDetailsElement = document.querySelector('[data-component-delivery-options-custom]')
    const deliveryOptionsSelectElement = deliveryOptionsDetailsElement?.querySelector(
      '[data-component-delivery-option-hidden-select]'
    )
    if (deliveryOptionsSelectElement) {
      const selectedOption = deliveryOptionsSelectElement.options[deliveryOptionsSelectElement.selectedIndex]
      const price = parseFloat(selectedOption.dataset.price) || 0
      this.setDeliveryAssistancePrice(price)
    }

    const shippingChargeElement = document.querySelector('product-shipping-charge-custom')
    const shippingChargeSelectElement = shippingChargeElement?.querySelector('select')
    if (shippingChargeSelectElement && deliveryOptionsDetailsElement) {
      if (deliveryOptionsDetailsElement?.hasAttribute('open')) {
        const selectedOption = shippingChargeSelectElement.options[shippingChargeSelectElement.selectedIndex]
        const price = parseFloat(selectedOption.dataset.price) || 0
        this.setDeliveryChargePrice(price)
      } else if (!deliveryOptionsDetailsElement.hasAttribute('open')) {
        this.setDeliveryChargePrice(0)
      }
    }
  }

  /**
   * Sets the price for the Christmas tree stand option.
   * @param {number} price - The price to set.
   */
  setChristMasTreeStandPrice(price) {
    this.additionalOptionsPrices.christMasTreeStand = price
  }

  /**
   * Sets the Christmas tree element.
   * @param {HTMLElement} element - The element to set.
   */
  setChristMasTreeElement(element) {
    this.christMasTreeElement = element
  }

  /**
   * Sets the collection element.
   * @param {HTMLElement} element - The element to set.
   */
  setCollectionElement(element) {
    this.collectionElement = element
  }

  /**
   * Sets the delivery options element.
   * @param {HTMLElement} element - The element to set.
   */
  setDeliveryOptionsElement(element) {
    this.deliveryOptionsElement = element
  }

  /**
   * Sets the click and collect element.
   * @param {HTMLElement} element - The element to set.
   */
  setClickAndCollectElement(element) {
    this.clickAndCollectElement = element
  }

  /**
   * Sets the product shipping charge element.
   * @param {HTMLElement} element - The element to set.
   */
  setProductShippingChargeElement(element) {
    this.productShippingChargeElement = element
  }

  /**
   * Sets the price for the collection option.
   * @param {number} price - The price to set.
   */
  setCollectionPrice(price) {
    this.additionalOptionsPrices.collection = price
  }

  /**
   * Sets the price for the delivery assistance option.
   * @param {number} price - The price to set.
   */
  setDeliveryAssistancePrice(price) {
    this.additionalOptionsPrices.deliveryAssistance = price
  }

  /**
   * Sets the price for the delivery charge option.
   * @param {number} price - The price to set.
   */
  setDeliveryChargePrice(price) {
    this.additionalOptionsPrices.deliveryCharge = price
  }

  /**
   * Sets the element that displays the subtotal.
   * @param {HTMLElement} element - The element to set.
   */
  setSubTotalElement(element) {
    this.subTotalElement = element
  }

  setPostCodePickerElement(element) {
    this.postCodePickerElement = element
  }

  /**
   * Sets the subtotal value in the DOM element.
   * @param {number} value - The subtotal value to set.
   */
  setSubTotalValue(value) {
    this.subTotalElement.textContent = `$${value}`
  }

  /**
   * Sets the total value of additional options by calculating their sum.
   */
  setAdditionalOptionsTotal() {
    this.setAdditionalOptionsPrices()
    this.additionalOptionsTotal = Object.values(this.additionalOptionsPrices).reduce((acc, price) => acc + price, 0)
  }

  /**
   * Updates the base product subtotal based on the variant selected.
   * @param {number} variantId - The variant ID to use for fetching product details.
   * @returns {Promise<void>}
   */
  async updateBaseProductSubTotal(variantId) {
    const productPageHtml = await fetchProductPage(this.productHandle, variantId)
    const newSubTotalElement = getSubTotalValueElementFromHtml(productPageHtml)
    this.subTotal = parseInt(newSubTotalElement.dataset.value, 10)
    this.setSubTotalValue(this.subTotal)
    this.subTotalElement.dataset.value = this.subTotal
    this.updateSubTotal()
  }

  /**
   * Adds additional options total to the base product subtotal and displays it.
   * Doesn't update the state of the subtotal.
   */
  updateSubTotal() {
    this.setAdditionalOptionsTotal()
    const newSubTotal = this.subTotal + this.additionalOptionsTotal
    this.setSubTotalValue(newSubTotal)
  }
}

customElements.define('product-form-custom', ProductFormCustom)
